import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import store from 'stores/store'
import { milliseconds } from 'date-fns'
import {
  fetchBirdeyeTokenOverview,
  TokenOverview,
} from './useBirdeyeTokenOverview'
import { birdeyeOverviewToPairSummary } from 'apis/birdeye/tokens'

export interface TokenPairSummaryResponse {
  base: TokenResponse
  quote: TokenResponse
  summary: TokenPairSummary
}

export interface TokenPairSummary {
  m5: PeriodSummary | null
  h1: PeriodSummary | null
  h6: PeriodSummary | null
  d1: PeriodSummary | null
}

export interface TokenResponse {
  mint: string
}

export interface Volume {
  volume: number
}

export interface Trades {
  trades: number
}

export interface PriceChange {
  change: number
}

export interface Amount {
  amount: number
}

export interface SplitBuySell<T> {
  buy: T
  sell: T
}

export interface PeriodSummary {
  amount: SplitBuySell<Amount>
  price: PriceChange
  trades: SplitBuySell<Trades>
  volume: SplitBuySell<Volume>
}

// Kept for back-compat with utils/tokens.ts#fetchMarketDataForMint. Now backed
// by Birdeye's defi/token_overview, normalized into the legacy shape.
export const fetchTokenPairSummary = async (
  baseMint: string,
  quoteMint: string,
): Promise<TokenPairSummaryResponse> => {
  const overview = await fetchBirdeyeTokenOverview(baseMint)
  const adapted = birdeyeOverviewToPairSummary(overview, baseMint, quoteMint)
  if (!adapted) {
    throw new Error(`No Birdeye overview for mint: ${baseMint}`)
  }
  return adapted
}

export function useTokenPairSummary(settings?: { refetchInterval?: number }) {
  const baseMint = store((s) => s.market.baseToken.mint)
  const quoteMint = store((s) => s.market.quoteToken.mint)

  // Same queryKey + queryFn as useBirdeyeTokenOverview so React Query
  // deduplicates the network request — both hooks read from one shared cache
  // entry and project it differently via `select`.
  const {
    data: tokenStats,
    isInitialLoading: loadingTokenStats,
    isFetching: fetchingTokenStats,
  } = useQuery({
    queryKey: ['birdeye-token-overview', baseMint],
    queryFn: () => fetchBirdeyeTokenOverview(baseMint),
    select: (overview: TokenOverview | undefined) =>
      birdeyeOverviewToPairSummary(overview, baseMint, quoteMint)?.summary,
    cacheTime: milliseconds({ minutes: 15 }),
    staleTime: milliseconds({ seconds: 5 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!baseMint && !!quoteMint,
    refetchInterval: settings?.refetchInterval,
  })

  const [
    change5mPercentage,
    change1hPercentage,
    change6hPercentage,
    change24hPercentage,
  ] = useMemo(
    () =>
      [
        tokenStats?.m5?.price?.change,
        tokenStats?.h1?.price?.change,
        tokenStats?.h6?.price?.change,
        tokenStats?.d1?.price?.change,
      ].map((periodChange) => {
        if (periodChange == null) return null
        return periodChange * 100
      }),
    [tokenStats],
  )

  return {
    change5mPercentage,
    change1hPercentage,
    change6hPercentage,
    change24hPercentage,
    loading: loadingTokenStats || fetchingTokenStats,
    isInitialLoading: loadingTokenStats,
    tokenStats,
  }
}
